import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  GlobalStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';
import API from '../../../API';
import useAuth from '../../../Hooks/use-auth';
import { useCreatorAuth } from '../../../Hooks/creator-use-auth';
import { generateBlitzPayPDF } from './downloadPdf/generateBlitzPayPDF';

// Import utilities and components
import {
  calculateDueDate,
  getDaysLate,
  getTotalDaysToPay,
  formatDate,
  formatDateTime,
  getStatusColor,
  getStatusText,
  getEffectiveValue,
  getCompanyInfo,
  getInvoiceNotes,
  getInvoiceTerms,
} from './invoicecomps/utils';
import CreatorInvoiceDesign from './invoicecomps/creatorinvoicedesign';
import RegularInvoiceDesign from './invoicecomps/regularinvoicedesign';
import BlitzPayInvoiceDesign from './invoicecomps/BlitzPayInvoiceDesign';
import ProjectInvoiceDesign from './invoicecomps/ProjectInvoiceDesign';
import BBCSplitInvoiceDesign from './bcc/blitzsplitinvoice';
import UnifiedInvoiceActionDialog from '../../BlitzPay/extras/unifiedinvoiceaction';
import InvoicePaymentDialog from '../../BlitzPay/extras/clientCreatorInvoiceSettlement';
import BBCSplitApprovalDialog from './bcc/blitzsplitapproval';

const InvoicePage = () => {
  const { id, invoiceId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Auth hooks
  const { getCurrrentUser, isAuthorized: isUserAuthorized } = useAuth();
  const { isAuthorized: isCreatorAuthorized, isInitialized: isCreatorInitialized } = useCreatorAuth();
  const userInfo = getCurrrentUser();

  const isAuthenticated = isUserAuthorized || isCreatorAuthorized;

  // State management
  const [invoiceData, setInvoiceData]         = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [blitzpayProfile, setBlitzpayProfile] = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [pdfGenerating, setPdfGenerating]     = useState(false);

  // Dialog states
  const [showUnifiedActionDialog, setShowUnifiedActionDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog]             = useState(false);
  const [showBBCApprovalDialog, setShowBBCApprovalDialog]     = useState(false);
  const [loginDialogOpen, setLoginDialogOpen]                 = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen]                     = useState(false);
  const [successMessage, setSuccessMessage]                   = useState('');

  const actualInvoiceId = id || invoiceId;
  const isCreatorRoute  = location.pathname.includes('/creatorinvoice/');
  const needsApproval   = searchParams.get('needsApproval') || searchParams.get('?needsApproval');

  /* ── Fetch invoice data ─────────────────────────────────────────── */
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        let data;

        if (isCreatorRoute) {
          data = await API.creatorConnect.getInvoice(actualInvoiceId);
          data = {
            invoice:      data || {},
            user:         null,
            company:      null,
            payouts:      [],
            creators:     [],
            invoice_type: 'creator',
          };
        } else {
          const basicData = await API.invoices.fetch(actualInvoiceId);
          if (basicData?.invoice?.invoice_type === 'project') {
            try {
              data = await API.invoices.fetchProjectInvoice(actualInvoiceId);
            } catch (e) {
              console.warn('Enhanced project data not available, using basic:', e);
              data = basicData;
            }
          } else {
            data = basicData;
          }
        }

        if (data) {
          data.payouts  = data.payouts  || [];
          data.creators = data.creators || [];
          data.invoice  = data.invoice  || {};
        }

        setInvoiceData(data);

        // Fetch custom invoice settings
        try {
          const settingsResp = await API.invoiceSettings.getPublic(actualInvoiceId);
          if (settingsResp?.settings) setInvoiceSettings(settingsResp.settings);
        } catch { /* no custom settings */ }

        // Fetch BlitzPay company profile
        const iType = data?.invoice?.invoice_type || data?.invoice_type;
        const isBP  = iType === 'blitzpay' || data?.invoice?.source_type === 'blitzpay';
        if (isBP) {
          if (data?.blitzpay_profile) {
            setBlitzpayProfile(data.blitzpay_profile);
          } else {
            setBlitzpayProfile(null);
          }
        } else {
          setBlitzpayProfile(null);
        }

      } catch (err) {
        console.error('Error fetching invoice:', err);
        setError('Failed to load invoice. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (actualInvoiceId) fetchInvoice();
  }, [actualInvoiceId, isCreatorRoute]);

  /* ── Approval URL param handler ─────────────────────────────────── */
  useEffect(() => {
    if (!needsApproval || loading || !invoiceData) return;

    const invoice = invoiceData?.invoice;
    const isAlreadyApproved =
      invoice?.status === 'approved' || invoice?.status === 'paid' || invoice?.approval_proof;
    if (isAlreadyApproved) return;

    const invoiceType  = invoice?.invoice_type || invoiceData?.invoice_type || 'client';
    const isBlitzPay   = invoiceType === 'blitzpay' || invoice?.source_type === 'blitzpay';
    const isBBC        = invoiceType === 'bbc'      || invoice?.source_type === 'bbc';
    const isCreatorInv = invoiceType === 'creator'  || isCreatorRoute;
    const isOwnInvoice = userInfo && invoice?.user_id === userInfo.id;
    const isIncoming   = isCreatorInv ||
                         invoice?.client_email === userInfo?.email ||
                         invoice?.client_id    === userInfo?.id;

    if (isOwnInvoice && !isBlitzPay && !isBBC && !isIncoming) return;

    // BBC invoices: identity gate inside BBCSplitInvoiceDesign handles
    // auth — don't open the modal dialog, let the inline page handle it.
    if (!userInfo) {
      if (!isBBC) setLoginDialogOpen(true);
      // BBC gate shows its own login wall
    } else if (isBlitzPay) {
      setShowUnifiedActionDialog(true);
    } else if (isBBC) {
      // BBC approval is handled inline via the sticky action bar —
      // no dialog needed. The needsApproval banner is shown in
      // BBCSplitInvoiceDesign itself.
    } else {
      setShowPaymentDialog(true);
    }
  }, [needsApproval, userInfo, loading, invoiceData, isCreatorRoute]);

  /* ── PDF download ───────────────────────────────────────────────── */
  const handleDownloadPDF = async () => {
    const invoiceType = invoiceData?.invoice?.invoice_type || invoiceData?.invoice_type;
    const isBlitzPay  = invoiceType === 'blitzpay' || invoiceData?.invoice?.source_type === 'blitzpay';

    /* BlitzPay: no auth required, programmatic PDF */
    if (isBlitzPay) {
      setPdfGenerating(true);
      try {
        generateBlitzPayPDF(
          invoiceData.invoice,
          blitzpayProfile,
          invoiceData.payouts || [],
          {
            company_name: invoiceData?.company?.name || invoiceData?.user?.company_name || '',
            contact_name: `${invoiceData?.user?.first_name || ''} ${invoiceData?.user?.last_name || ''}`.trim(),
            email: invoiceData?.user?.email || '',
          },
        );
        toast.success('Invoice PDF downloaded');
      } catch (err) {
        console.error('BlitzPay PDF error:', err);
        toast.error('Failed to generate PDF. Please try again.');
      } finally {
        setPdfGenerating(false);
      }
      return;
    }

    /* Non-BlitzPay: require auth, capture #invoice-content element */
    if (!isAuthenticated) {
      setLoginDialogOpen(true);
      return;
    }

    setPdfGenerating(true);
    try {
      await captureInvoiceContentPDF(invoiceData, actualInvoiceId, invoiceType);
    } catch (err) {
      console.error('PDF capture error:', err);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  /* ── Misc handlers ──────────────────────────────────────────────── */
  const handleCopyInvoiceNumber = () => {
    navigator.clipboard.writeText(invoiceData?.invoice?.po_number || actualInvoiceId);
    toast.success('Invoice number copied to clipboard');
  };

  const handleShareInvoice = async () => {
    const url = window.location.href;
    const invoiceNumber = invoiceData?.invoice?.po_number || actualInvoiceId;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Invoice ${invoiceNumber}`,
          text: `View invoice ${invoiceNumber}`,
          url,
        });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }

    await navigator.clipboard.writeText(url);
    toast.success('Invoice link copied to clipboard');
  };

  const handleApprovalSuccess = (message) => {
    setSuccessMessage(message || 'Invoice approved successfully!');
    setShowUnifiedActionDialog(false);
    setShowPaymentDialog(false);
    setShowBBCApprovalDialog(false);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('needsApproval');
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    setTimeout(() => window.location.reload(), 1500);
  };

  const handleCloseApprovalDialog = () => {
    setShowUnifiedActionDialog(false);
    setShowPaymentDialog(false);
    setShowBBCApprovalDialog(false);
  };

  const unifiedInvoiceActionMutation = async (payload) => {
    try   { return await API.invoices.unifiedAction(payload); }
    catch (err) { console.error(err); throw err; }
  };

  /* ── Loading ────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>Loading invoice…</Typography>
        </Box>
      </Box>
    );
  }

  /* ── Error ──────────────────────────────────────────────────────── */
  if (error || !invoiceData?.invoice) {
    return (
      <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          <Typography variant="h6">Invoice Not Found</Typography>
          <Typography>{error || 'The requested invoice could not be found.'}</Typography>
        </Alert>
      </Box>
    );
  }

  /* ── Prepare shared props ───────────────────────────────────────── */
  const { invoice, user, company, payouts = [], invoice_type } = invoiceData;

  const invoiceType       = invoice?.invoice_type || invoice_type || 'client';
  const isBlitzPayInvoice = invoiceType === 'blitzpay' || invoice?.source_type === 'blitzpay';
  const isBBCInvoice      = invoiceType === 'bbc'      || invoice?.source_type === 'bbc';
  const isAlreadyApproved = invoice?.status === 'approved' || invoice?.status === 'paid' || invoice?.approval_proof;
  const shouldShowApprovalBanner = needsApproval && !isAlreadyApproved;

  const daysLate          = getDaysLate(invoice);
  const totalDaysToPay    = getTotalDaysToPay(invoice);
  const calculatedDueDate = calculateDueDate(invoice);
  const companyInfo       = getCompanyInfo(invoiceData, invoiceSettings);

  const boundGetEffectiveValue = (key, fallback) => getEffectiveValue(invoiceData, invoiceSettings, key, fallback);
  const boundGetInvoiceNotes   = ()               => getInvoiceNotes(invoice, invoiceSettings);
  const boundGetInvoiceTerms   = ()               => getInvoiceTerms(invoice, invoiceSettings);

  const commonProps = {
    invoice,
    user,
    company,
    companyInfo,
    invoiceSettings,
    blitzpayProfile,
    payouts,
    onDownloadPDF:        handleDownloadPDF,
    onCopyInvoiceNumber:  handleCopyInvoiceNumber,
    onShareInvoice:       handleShareInvoice,
    pdfGenerating,
    formatDate,
    formatDateTime,
    getStatusColor,
    getStatusText,
    getEffectiveValue:    boundGetEffectiveValue,
    getInvoiceNotes:      boundGetInvoiceNotes,
    getInvoiceTerms:      boundGetInvoiceTerms,
    daysLate,
    totalDaysToPay,
    calculatedDueDate,
    supportingDocument:   invoice.supporting_document,
    onOpenPdfViewer:      () => setPdfDialogOpen(true),
    needsApproval:        shouldShowApprovalBanner,
    userInfo,
    onOpenApprovalDialog: () => {
      if (isBlitzPayInvoice) setShowUnifiedActionDialog(true);
      else if (isBBCInvoice) setShowBBCApprovalDialog(true);
      else                   setShowPaymentDialog(true);
    },
    onOpenLoginDialog:    () => setLoginDialogOpen(true),
    isAuthenticated,
    isCreatorAuthorized,
  };

  /* ── Render invoice design ──────────────────────────────────────── */
  const renderInvoiceDesign = () => {
    switch (invoiceType) {
      case 'creator':
        return (
          <CreatorInvoiceDesign
            {...commonProps}
            billedToEmail={invoice?.client_email}
            billedToName={invoice?.client_name}
          />
        );

      case 'bbc':
        return (
          <BBCSplitInvoiceDesign
            {...commonProps}
            bbcSummary={invoiceData?.bbc_summary}
            creatorPayments={invoiceData?.creator_payments || []}
            billedToEmail={invoice?.client_email}
            billedToName={invoice?.client_name}
          />
        );

      case 'blitzpay':
        return (
          <BlitzPayInvoiceDesign
            {...commonProps}
            billedToEmail={invoice?.client_email}
            billedToName={invoice?.client_name}
          />
        );

      case 'project':
        return (
          <ProjectInvoiceDesign
            {...commonProps}
            billedToEmail={invoiceData?.client_info?.email || invoice?.client_email}
            billedToName={invoiceData?.client_info?.full_name || invoice?.client_name}
            projectInfo={invoiceData?.project_info}
            clientInfo={invoiceData?.client_info}
            creatorNames={invoiceData?.creator_names}
            lineItems={invoiceData?.line_items}
          />
        );

      case 'client':
      case 'partial':
      default:
        return (
          <RegularInvoiceDesign
            {...commonProps}
            billedToEmail={invoice?.client_email || user?.email}
            billedToName={
              invoice?.client_name ||
              (user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '')
            }
          />
        );
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'html, body': {
              margin: 0,
              padding: 0,
              background: '#fff !important',
            },
            'body *': {
              visibility: 'hidden',
            },
            '#invoice-content, #invoice-content *': {
              visibility: 'visible',
            },
            '#invoice-content': {
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              margin: 0,
              boxShadow: 'none !important',
              background: '#fff !important',
              overflow: 'visible !important',
            },
          },
        }}
      />
      {renderInvoiceDesign()}

      {/* BBCSplitApprovalDialog — used from BlitzPay.jsx pending items,
          NOT from the invoice page itself (which uses inline actions) */}
      {showBBCApprovalDialog && (
        <BBCSplitApprovalDialog
          open={showBBCApprovalDialog}
          onClose={handleCloseApprovalDialog}
          invoice={invoice}
          bbcSummary={invoiceData?.bbc_summary}
          creatorPayments={invoiceData?.creator_payments || []}
          onSuccess={handleApprovalSuccess}
        />
      )}

      {/* InvoicePaymentDialog — client/creator/project */}
      {showPaymentDialog && (
        <InvoicePaymentDialog
          open={showPaymentDialog}
          onClose={handleCloseApprovalDialog}
          invoice={invoice}
          onSuccess={handleApprovalSuccess}
        />
      )}

      {/* UnifiedInvoiceActionDialog — BlitzPay */}
      {showUnifiedActionDialog && (
        <UnifiedInvoiceActionDialog
          open={showUnifiedActionDialog}
          onClose={handleCloseApprovalDialog}
          invoiceId={actualInvoiceId}
          onSuccess={handleApprovalSuccess}
          unifiedInvoiceActionMutation={unifiedInvoiceActionMutation}
          invoiceAmount={invoice?.amount || 0}
          invoiceType={invoiceType}
          requiresApprovalProof={true}
        />
      )}

      {/* Login Dialog — non-BBC types only; BBC shows its own gate */}
      {!isBBCInvoice && (
        <Dialog
          open={loginDialogOpen}
          onClose={() => setLoginDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
        >
          <DialogTitle>
            <Typography variant="h5" fontWeight={700}>Sign In Required</Typography>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              You must be logged in to access this feature. Please login or create an account to continue.
            </Typography>
            <Alert severity="info">After logging in, you'll be able to approve and pay invoices.</Alert>
          </DialogContent>
          <DialogActions sx={{ gap: 1, pt: 2 }}>
            <Button onClick={() => setLoginDialogOpen(false)} variant="outlined">Cancel</Button>
            <Button
              onClick={() => navigate('/login', { state: { returnTo: location.pathname + location.search } })}
              variant="outlined"
            >
              Login
            </Button>
            <Button
              onClick={() => navigate('/onboard', { state: { returnTo: location.pathname + location.search } })}
              variant="contained"
            >
              Create Account
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Success Dialog */}
      {successMessage && (
        <Dialog open={Boolean(successMessage)} onClose={() => setSuccessMessage('')}>
          <DialogContent>
            <Alert severity="success">
              <Typography variant="h6">{successMessage}</Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSuccessMessage('')} variant="contained">Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Supporting document viewer */}
      <Dialog open={pdfDialogOpen} onClose={() => setPdfDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Supporting Documents</DialogTitle>
        <DialogContent>
          {invoice.supporting_document && (
            <iframe
              src={invoice.supporting_document}
              style={{ width: '100%', height: '70vh', border: 'none' }}
              title="Supporting Document PDF"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPdfDialogOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/* ════════════════════════════════════════════════════════════════════
   captureInvoiceContentPDF — for non-BlitzPay types.
   Targets ONLY the #invoice-content Paper element so no nav, no
   gradient background, no action bars bleed into the PDF.
   ════════════════════════════════════════════════════════════════════ */
function buildSectionPageGroups(invoiceEl, maxPageContentCssPx) {
  const sections = Array.from(invoiceEl.children).filter((child) => {
    if (!(child instanceof HTMLElement)) return false;
    if (child.offsetHeight <= 0) return false;
    return window.getComputedStyle(child).display !== 'none';
  });

  if (!sections.length) return null;

  const pageGroups = [];
  let currentGroup = [];
  let currentGroupStart = 0;

  for (const section of sections) {
    const sectionTop    = section.offsetTop;
    const sectionBottom = sectionTop + section.offsetHeight;

    if (!currentGroup.length) {
      currentGroup      = [section];
      currentGroupStart = sectionTop;
      continue;
    }

    if (sectionBottom - currentGroupStart <= maxPageContentCssPx) {
      currentGroup.push(section);
      continue;
    }

    pageGroups.push(currentGroup);
    currentGroup      = [section];
    currentGroupStart = sectionTop;
  }

  if (currentGroup.length) {
    pageGroups.push(currentGroup);
  }

  return pageGroups;
}

async function renderSectionGroupCanvas(invoiceEl, sections) {
  const pageEl = invoiceEl.cloneNode(false);
  pageEl.removeAttribute('id');
  pageEl.style.width          = `${invoiceEl.scrollWidth}px`;
  pageEl.style.minHeight      = 'auto';
  pageEl.style.height         = 'auto';
  pageEl.style.margin         = '0';
  pageEl.style.boxShadow      = 'none';
  pageEl.style.overflow       = 'visible';
  pageEl.style.position       = 'fixed';
  pageEl.style.left           = '-10000px';
  pageEl.style.top            = '0';
  pageEl.style.zIndex         = '-1';

  sections.forEach((section) => {
    pageEl.appendChild(section.cloneNode(true));
  });

  document.body.appendChild(pageEl);
  await new Promise((resolve) => setTimeout(resolve, 100));

  try {
    const captureWindowWidth  = Math.max(window.innerWidth,  pageEl.scrollWidth);
    const captureWindowHeight = Math.max(window.innerHeight, pageEl.scrollHeight);

    return await html2canvas(pageEl, {
      scale:           2.5,
      useCORS:         true,
      allowTaint:      true,
      logging:         false,
      windowWidth:     captureWindowWidth,
      windowHeight:    captureWindowHeight,
      width:           pageEl.scrollWidth,
      height:          pageEl.scrollHeight,
      x:               0,
      y:               0,
      scrollX:         0,
      scrollY:         0,
      backgroundColor: '#ffffff',
      imageTimeout:    10000,
      removeContainer: true,
      letterRendering: true,
    });
  } finally {
    document.body.removeChild(pageEl);
  }
}

function addCanvasRangeToPdf(pdf, canvas, startPx, endPx, margins, usableW) {
  const safeStartPx   = Math.max(0, Math.floor(startPx));
  const safeEndPx     = Math.min(canvas.height, Math.ceil(endPx));
  const sliceHeightPx = safeEndPx - safeStartPx;
  if (sliceHeightPx <= 0) return;

  const sliceCanvas      = document.createElement('canvas');
  sliceCanvas.width      = canvas.width;
  sliceCanvas.height     = sliceHeightPx;

  const sliceCtx = sliceCanvas.getContext('2d');
  if (!sliceCtx) throw new Error('Failed to create PDF slice canvas');

  sliceCtx.drawImage(canvas, 0, safeStartPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);

  const sliceData = sliceCanvas.toDataURL('image/png', 0.95);
  const sliceH    = (sliceHeightPx * usableW) / canvas.width;
  pdf.addImage(sliceData, 'PNG', margins, margins, usableW, sliceH, undefined, 'FAST');
}

function addCanvasToSinglePdfPage(pdf, canvas, margins, usableW, pageH, addPageBefore = false) {
  if (addPageBefore) {
    pdf.addPage();
  }

  const imgData           = canvas.toDataURL('image/png', 0.95);
  const pageContentHeight = pageH - margins * 2;

  let imgW = usableW;
  let imgH = (canvas.height * imgW) / canvas.width;

  if (imgH > pageContentHeight) {
    imgH = pageContentHeight;
    imgW = (canvas.width * imgH) / canvas.height;
  }

  const offsetX = margins + ((usableW - imgW) / 2);
  pdf.addImage(imgData, 'PNG', offsetX, margins, imgW, imgH, undefined, 'FAST');
}

async function captureInvoiceContentPDF(invoiceData, actualInvoiceId, invoiceType) {
  const invoiceEl     = document.getElementById('invoice-content');
  const approvalEl    = document.getElementById('approval-stamp-section');
  const supportingDoc = invoiceData?.invoice?.supporting_document;

  if (!invoiceEl) throw new Error('invoice-content element not found');

  // Temporarily hide approval stamp so it is captured separately
  let approvalPrevDisplay = null;
  if (approvalEl) {
    approvalPrevDisplay      = approvalEl.style.display;
    approvalEl.style.display = 'none';
  }

  // Allow layout to settle before capture
  await new Promise((r) => setTimeout(r, 300));

  const captureWindowWidth  = Math.max(window.innerWidth,  invoiceEl.scrollWidth);
  const captureWindowHeight = Math.max(window.innerHeight, invoiceEl.scrollHeight);

  const canvas = await html2canvas(invoiceEl, {
    scale:           2.5,
    useCORS:         true,
    allowTaint:      true,
    logging:         false,
    windowWidth:     captureWindowWidth,
    windowHeight:    captureWindowHeight,
    width:           invoiceEl.scrollWidth,
    height:          invoiceEl.scrollHeight,
    x:               0,
    y:               0,
    scrollX:         0,
    scrollY:         0,
    backgroundColor: '#ffffff',
    imageTimeout:    10000,
    removeContainer: true,
    letterRendering: true,
  });

  // Restore approval section
  if (approvalEl && approvalPrevDisplay !== null) {
    approvalEl.style.display = approvalPrevDisplay;
  }

  const pdf               = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const pageW             = pdf.internal.pageSize.getWidth();
  const pageH             = pdf.internal.pageSize.getHeight();
  const margins           = 10;
  const usableW           = pageW - margins * 2;
  const pageContentHeight = pageH - margins * 2;

  const projectPageGroups =
    invoiceType === 'project'
      ? buildSectionPageGroups(invoiceEl, (pageContentHeight * invoiceEl.scrollWidth) / usableW)
      : null;

  if (projectPageGroups?.length) {
    for (const [index, group] of projectPageGroups.entries()) {
      const groupCanvas = await renderSectionGroupCanvas(invoiceEl, group);
      addCanvasToSinglePdfPage(pdf, groupCanvas, margins, usableW, pageH, index > 0);
    }
  } else {
    const imgData  = canvas.toDataURL('image/png', 0.95);
    const imgProps = pdf.getImageProperties(imgData);
    const imgH     = (imgProps.height * usableW) / imgProps.width;

    if (imgH <= pageContentHeight) {
      pdf.addImage(imgData, 'PNG', margins, margins, usableW, imgH, undefined, 'FAST');
    } else {
      const totalPages = Math.ceil(imgH / pageContentHeight);
      for (let pg = 0; pg < totalPages; pg++) {
        if (pg > 0) pdf.addPage();
        const srcY = (pg * pageContentHeight * imgProps.width) / usableW;
        const srcH = Math.min(
          (pageContentHeight * imgProps.width) / usableW,
          imgProps.height - srcY,
        );
        addCanvasRangeToPdf(pdf, canvas, srcY, srcY + srcH, margins, usableW);
      }
    }
  }

  // Approval stamp — second page if present
  if (approvalEl && invoiceData?.invoice?.approval_proof) {
    pdf.addPage();
    if (approvalEl) approvalEl.style.display = approvalPrevDisplay || 'block';
    await new Promise((r) => setTimeout(r, 200));

    const aCanvas = await html2canvas(approvalEl, {
      scale: 2.5, useCORS: true, allowTaint: true, logging: false,
      backgroundColor: '#ffffff', imageTimeout: 10000,
    });

    if (approvalEl) approvalEl.style.display = 'none';

    const aImgData  = aCanvas.toDataURL('image/png', 0.95);
    const aImgProps = pdf.getImageProperties(aImgData);
    const aH        = (aImgProps.height * usableW) / aImgProps.width;
    pdf.addImage(aImgData, 'PNG', margins, margins, usableW, aH, undefined, 'FAST');

    if (approvalEl && approvalPrevDisplay !== null) {
      approvalEl.style.display = approvalPrevDisplay;
    }
  }

  const filename = `Invoice_${invoiceData?.invoice?.po_number || actualInvoiceId}.pdf`;

  // Try to merge with supporting PDF
  if (supportingDoc && supportingDoc.toLowerCase().endsWith('.pdf')) {
    try {
      const { PDFDocument } = await import('pdf-lib');
      const invoiceBytes    = pdf.output('arraybuffer');
      const supportingResp  = await fetch(supportingDoc);
      if (!supportingResp.ok) throw new Error('Fetch failed');
      const supportingBytes = await supportingResp.arrayBuffer();

      const invDoc  = await PDFDocument.load(invoiceBytes);
      const supDoc  = await PDFDocument.load(supportingBytes);
      const merged  = await PDFDocument.create();

      const iPages = await merged.copyPages(invDoc, invDoc.getPageIndices());
      iPages.forEach((p) => merged.addPage(p));
      const sPages = await merged.copyPages(supDoc, supDoc.getPageIndices());
      sPages.forEach((p) => merged.addPage(p));

      const bytes = await merged.save();
      const blob  = new Blob([bytes], { type: 'application/pdf' });
      const url   = URL.createObjectURL(blob);
      const a     = document.createElement('a');
      a.href      = url;
      a.download  = filename.replace('.pdf', '_with_attachments.pdf');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded with attachments');
    } catch (mergeErr) {
      console.warn('PDF merge failed, downloading separately:', mergeErr);
      pdf.save(filename);
      toast.success('Invoice PDF downloaded');
      await new Promise((r) => setTimeout(r, 500));
      await downloadSupportingDocument(supportingDoc, invoiceData, actualInvoiceId);
    }
  } else {
    pdf.save(filename);
    toast.success('Invoice PDF downloaded');
    if (supportingDoc) {
      await new Promise((r) => setTimeout(r, 500));
      await downloadSupportingDocument(supportingDoc, invoiceData, actualInvoiceId);
    }
  }
}

async function downloadSupportingDocument(supportingDoc, invoiceData, actualInvoiceId) {
  try {
    const resp = await fetch(supportingDoc);
    const url  = URL.createObjectURL(await resp.blob());
    const ext  = new URL(supportingDoc).pathname.split('.').pop() || 'pdf';
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `invoice_${invoiceData?.invoice?.po_number || actualInvoiceId}_supporting.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    window.open(supportingDoc, '_blank');
  }
}

export default InvoicePage;